import axios from "axios";

import { CategoryApiResponse } from "./categoryApi";
import { SubCategoryApiResponse } from "./subCategoryApi";
import { TagApiResponse } from "./tagApi";
import { SubTagApiResponse } from "./subTagApi";
import { PositionApiResponse } from "./positionApi";
import { ReferenceApiResponse } from "./referenceApi";
import { API_CALL_URL } from "../constants";
import { AffiliateApiResponse } from "./affiliateApi";

export type YoutubeContentApiResponse = {
  id: string;
  contentName: string;
  url: string;
  summary: string;
  thumbnail: string;
  orderIndex: number;
  categoryId: string;
  subCategoryId: string;
  tagId: string;
  subTagId: string;
  referenceId: string;
  affiliateId: string;
  createdAt: Date;
  updatedAt: Date;
  category?: CategoryApiResponse;
  subCategory?: SubCategoryApiResponse;
  tag?: TagApiResponse;
  subTag?: SubTagApiResponse;
  positions?: PositionApiResponse[];
  reference?: ReferenceApiResponse;
  affiliate?: AffiliateApiResponse;
};

export type YoutubeContentUpsertRequest = {
  id?: string;
  contentName: string;
  url: string | null;
  summary: string | null;
  orderIndex: number | null;
  categoryId: string | null;
  subCategoryId: string | null;
  tagId: string | null;
  subTagId: string | null;
  referenceId: string | null;
  affiliateId: string | null;
};

export type YoutubeContentAddPositionsRequest = {
  id: string;
  positionIds: string[];
};

export type YoutubeContentThumbnailUploadRequest = {
  id: string;
  thumbnail: string;
};

export const fetchYoutubeContent = async (): Promise<YoutubeContentApiResponse[]> => {
  try {
    const response = await axios.get<YoutubeContentApiResponse[]>(
      `${API_CALL_URL}/youtube-content`,
    );
    return response.data;
  } catch (error) {
    console.error("Axios error:", error);
    return [];
  }
};

export const upsertYoutubeContent = async ({
  id,
  contentName,
  url,
  summary,
  orderIndex,
  categoryId,
  subCategoryId,
  tagId,
  subTagId,
  referenceId,
  affiliateId,
}: YoutubeContentUpsertRequest): Promise<YoutubeContentApiResponse | undefined> => {
  try {
    const response = await axios.post<YoutubeContentApiResponse>(
      `${API_CALL_URL}/youtube-content`,
      {
        id: id ?? undefined,
        contentName,
        url,
        summary,
        orderIndex,
        categoryId,
        subCategoryId,
        tagId,
        subTagId,
        referenceId,
        affiliateId,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Axios error:", error);
    return undefined;
  }
};

export const deleteYoutubeContent = async ({ id }: { id: string }): Promise<boolean> => {
  try {
    await axios.delete(`${API_CALL_URL}/youtube-content/${id}`);
    return true;
  } catch (error) {
    console.error("Axios error:", error);
    return false;
  }
};

export const addPositionsToYoutubeContent = async ({
  id,
  positionIds,
}: YoutubeContentAddPositionsRequest): Promise<YoutubeContentApiResponse | undefined> => {
  try {
    const response = await axios.put<YoutubeContentApiResponse>(
      `${API_CALL_URL}/youtube-content/add-positions`,
      {
        id,
        positionIds,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Axios error:", error);
    return undefined;
  }
};

export const removePositionsFromYoutubeContent = async ({
  id,
}: {
  id: string;
}): Promise<boolean> => {
  try {
    await axios.delete(`${API_CALL_URL}/youtube-content/delete-positions/${id}`);
    return true;
  } catch (error) {
    console.error("Axios error:", error);
    return false;
  }
};

export const upsertYoutubeContentThumbnail = async ({
  id,
  thumbnail,
}: YoutubeContentThumbnailUploadRequest): Promise<YoutubeContentApiResponse | undefined> => {
  try {
    const response = await axios.post<YoutubeContentApiResponse>(
      `${API_CALL_URL}/youtube-content/upsert-thumbnail`,
      {
        id,
        thumbnail,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Axios error:", error);
    return undefined;
  }
};
